import { useEffect, useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { XIcon, DocumentTextIcon } from "@heroicons/react/outline";
import { DocumentData, SupportingDocumentMetadata } from "types";
import { useClient } from "utils/client";
import { fetchDocumentBytes } from "utils/documentZip";
import { supportingFilename } from "./supportingDocumentFiles";
import {
  supportingCategories,
  supportingManagementPath,
} from "./supportingMetadata";

export const SupportingDocumentDialog = ({
  projectId,
  initial,
  doc,
  file,
  onClose,
  onSave,
  busy = false,
  error = false,
  readOnly = false,
  position,
}: {
  projectId: string;
  initial: SupportingDocumentMetadata;
  doc?: DocumentData;
  file?: File;
  onClose: () => void;
  onSave?: (metadata: SupportingDocumentMetadata) => void;
  busy?: boolean;
  error?: boolean;
  readOnly?: boolean;
  position?: string;
}) => {
  const { t } = useTranslation();
  const client = useClient();
  const clientRef = useRef(client);
  clientRef.current = client;
  const [metadata, setMetadata] = useState(initial);
  const initialRef = useRef(initial);
  initialRef.current = initial;
  useEffect(() => {
    setMetadata(initialRef.current);
    setPreview(undefined);
    setPreviewFailed(false);
  }, [file, doc?.name]);
  const [preview, setPreview] = useState<string>();
  const [previewFailed, setPreviewFailed] = useState(false);
  const filename = file?.name || (doc ? supportingFilename(doc) : "");
  const ext = filename.split(".").pop()?.toLowerCase();
  const mime =
    ext === "pdf"
      ? "application/pdf"
      : (
          {
            png: "image/png",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            webp: "image/webp",
            gif: "image/gif",
          } as Record<string, string>
        )[ext || ""];
  useEffect(() => {
    if (!mime) return;
    let cancelled = false;
    let url: string | undefined;
    const load = async () => {
      try {
        const bytes =
          file ||
          (doc &&
            (await fetchDocumentBytes(doc, projectId, clientRef.current)));
        if (!bytes || cancelled) return;
        url = URL.createObjectURL(new Blob([bytes], { type: mime }));
        setPreview(url);
      } catch {
        if (!cancelled) setPreviewFailed(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc, file, projectId, mime]);
  const change = (key: keyof SupportingDocumentMetadata, value: string) =>
    setMetadata((previous) => ({ ...previous, [key]: value }));
  const inputClass =
    "mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500 disabled:bg-gray-50 disabled:text-gray-600";
  const field = (
    key: keyof SupportingDocumentMetadata,
    type = "text",
    maxLength = 240,
  ) => (
    <label className="block text-sm font-medium text-gray-700" key={key}>
      {t(`documentLibrary.fields.${key}`)}
      {key === "title" && " *"}
      <input
        type={type}
        value={metadata[key] || ""}
        required={key === "title"}
        maxLength={maxLength}
        onChange={(e) => change(key, e.target.value)}
        className={inputClass}
      />
    </label>
  );
  const select = (
    key: keyof SupportingDocumentMetadata,
    values: string[],
    labels: string,
  ) => (
    <label className="block text-sm font-medium text-gray-700">
      {t(`documentLibrary.fields.${key}`)}
      {key === "category" && " *"}
      <select
        value={metadata[key] || ""}
        className={inputClass}
        onChange={(e) => {
          const value = e.target.value;
          setMetadata((previous) => ({
            ...previous,
            [key]: value,
            ...(key === "category"
              ? {
                  visibility:
                    value === "supplier_manuals" ? "external" : "internal",
                }
              : {}),
          }));
        }}
      >
        {values.map((value) => (
          <option key={value} value={value}>
            {value ? t(`${labels}.${value}`) : t("documentLibrary.unspecified")}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center p-3 sm:p-6">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900 bg-opacity-40" />
        <div className="relative w-full max-w-4xl rounded-xl bg-white shadow-xl">
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-5 sm:p-6">
            <div className="min-w-0">
              <p className="mb-1 text-xs font-medium text-cyan-700">
                {t(
                  file
                    ? "documentLibrary.newUpload"
                    : "documentLibrary.projectUpload",
                )}
                {position && ` · ${position}`}
              </p>
              <Dialog.Title className="break-words text-xl font-semibold text-gray-900">
                {file ? t("documentLibrary.upload") : initial.title}
              </Dialog.Title>
              <p className="mt-1 break-all text-xs text-gray-500">
                {filename}
                {file?.size || doc?.file_size
                  ? ` · ${((file?.size || doc?.file_size || 0) / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB`
                  : ""}
              </p>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              aria-label={t("documentLibrary.close")}
              className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-6 p-5 sm:p-6 lg:grid-cols-3">
            <aside className="min-w-0">
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                {preview ? (
                  mime === "application/pdf" ? (
                    <object
                      data={preview}
                      type="application/pdf"
                      aria-label={t("documentLibrary.preview")}
                      className="h-80 w-full"
                    >
                      <p className="p-4 text-sm">
                        {t("documentLibrary.previewUnavailable")}
                      </p>
                    </object>
                  ) : (
                    <img
                      src={preview}
                      alt={initial.title}
                      className="max-h-80 w-full object-contain"
                    />
                  )
                ) : (
                  <div className="flex min-h-[160px] flex-col items-center justify-center gap-3 p-5 text-center text-sm text-gray-500">
                    <DocumentTextIcon className="h-9 w-9" />
                    <span>
                      {t(
                        mime && !previewFailed
                          ? "documentLibrary.previewLoading"
                          : "documentLibrary.previewUnavailable",
                      )}
                    </span>
                  </div>
                )}
              </div>
              <p className="mt-4 text-xs leading-5 text-gray-500">
                {t("documentLibrary.projectScope")}
              </p>
              {doc?.uploaded_at && (
                <p className="mt-4 text-xs text-gray-500">
                  {t("documentLibrary.uploadedAt")}:{" "}
                  {new Date(doc.uploaded_at).toLocaleDateString()}
                </p>
              )}
            </aside>
            <form
              className="min-w-0 lg:col-span-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!busy && !readOnly && metadata.title.trim())
                  onSave?.({ ...metadata, title: metadata.title.trim() });
              }}
            >
              <fieldset
                disabled={busy || readOnly}
                className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              >
                <div className="sm:col-span-2">{field("title")}</div>
                {select(
                  "category",
                  [...supportingCategories],
                  "documentLibrary.categories",
                )}
                {select(
                  "visibility",
                  ["internal", "external"],
                  "documentLibrary.visibility",
                )}
                {field("document_number")}
                {field("component")}
                {select(
                  "origin",
                  ["", "internal", "supplier", "laboratory", "customer"],
                  "documentLibrary.origins",
                )}
                {select(
                  "language",
                  [
                    "",
                    "de",
                    "en",
                    "fr",
                    "es",
                    "it",
                    "pt",
                    "pl",
                    "hu",
                    "sk",
                    "sl",
                    "sv",
                    "multilingual",
                    ...(metadata.language &&
                    ![
                      "de",
                      "en",
                      "fr",
                      "es",
                      "it",
                      "pt",
                      "pl",
                      "hu",
                      "sk",
                      "sl",
                      "sv",
                      "multilingual",
                    ].includes(metadata.language)
                      ? [metadata.language]
                      : []),
                  ],
                  "languages",
                )}
                {field("edition")}
                {field("document_date", "date")}
                {field("valid_until", "date")}
                <label className="block text-sm font-medium text-gray-700 sm:col-span-2">
                  {t("documentLibrary.fields.notes")}
                  <textarea
                    rows={3}
                    value={metadata.notes || ""}
                    maxLength={4000}
                    onChange={(e) => change("notes", e.target.value)}
                    className={inputClass}
                  />
                </label>
              </fieldset>
              <p className="mt-4 text-xs text-gray-500">
                {t("documentLibrary.exportLocation")}:{" "}
                {t(`documentLibrary.visibility.${metadata.visibility}`)} /{" "}
                {t(`documentLibrary.categories.${metadata.category}`)}
              </p>
              {error && (
                <p
                  role="alert"
                  className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700"
                >
                  {t("supportingDocuments.actionFailed")}
                </p>
              )}
              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 disabled:opacity-50"
                >
                  {t("documentLibrary.close")}
                </button>
                {readOnly ? (
                  <Link
                    to={supportingManagementPath(projectId)}
                    className="rounded-md bg-cyan-600 px-4 py-2 text-sm text-white"
                  >
                    {t("supportingDocuments.manage")}
                  </Link>
                ) : (
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
                  >
                    {t(
                      busy
                        ? "documentLibrary.saving"
                        : file
                          ? "documentLibrary.upload"
                          : "documentLibrary.save",
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
